import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import { LikeButton } from '../components/LikeButton';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

interface ResultState {
  won?: boolean;
  score?: number;
  dezoomsUsed?: number;
  durationSec?: number;
  shareText?: string | null;
  puzzleNumber?: number;
  puzzleId?: string;
  fromRun?: string;
}

interface RunSummary {
  won: boolean;
  score: number;
  dezoomsUsed: number;
  durationSec: number;
  puzzleNumber: number;
  puzzleId: string;
  shareText?: string | null;
  likeCount: number;
  liked: boolean;
}

export function ResultPage() {
  const location = useLocation();
  const state = location.state as ResultState | null;
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [summary, setSummary] = useState<RunSummary | null>(null);
  const [loading, setLoading] = useState(!!state?.fromRun && !state.dezoomsUsed);

  useEffect(() => {
    if (state?.fromRun && state.dezoomsUsed == null) {
      api<RunSummary>(`/runs/${state.fromRun}/summary`)
        .then((data) => {
          setSummary(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [state]);

  const won = summary?.won ?? state?.won ?? false;
  const score = summary?.score ?? state?.score ?? 0;
  const dezoomsUsed = summary?.dezoomsUsed ?? state?.dezoomsUsed ?? 0;
  const durationSec = summary?.durationSec ?? state?.durationSec ?? 0;
  const puzzleNumber = summary?.puzzleNumber ?? state?.puzzleNumber ?? 0;
  const puzzleId = summary?.puzzleId ?? state?.puzzleId;
  const shareText = summary?.shareText ?? state?.shareText ?? null;

  if (loading) {
    return <main className="page result"><p className="loading">Chargement…</p></main>;
  }

  if (!state && !summary) {
    return (
      <main className="page result">
        <div className="result-card">
          <p>Pas de résultat.</p>
          <Link to="/play" className="btn primary">Rejouer</Link>
        </div>
      </main>
    );
  }

  const handleShare = async () => {
    if (!shareText) return;
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent */
    }
  };

  const mins = Math.floor(durationSec / 60);
  const secs = durationSec % 60;

  return (
    <main className="page result">
      <div className="result-card">
        <h1 className={won ? 'win' : 'fail'}>
          {won ? t('result.win') : t('result.fail')}
        </h1>
        {won && <p className="score">{score} pts</p>}
        <ul className="stats">
          <li><strong>{dezoomsUsed}</strong> dézooms</li>
          <li><strong>{mins}:{secs.toString().padStart(2, '0')}</strong> temps</li>
          <li><strong>#{puzzleNumber}</strong> énigme</li>
        </ul>

        {puzzleId && (
          <LikeButton
            puzzleId={puzzleId}
            initialCount={summary?.likeCount}
            initialLiked={summary?.liked}
          />
        )}

        {shareText && (
          <button type="button" className="btn primary" onClick={handleShare}>
            {copied ? t('result.copied') : t('result.share')}
          </button>
        )}
        {shareText && (
          <pre className="share-preview">{shareText}</pre>
        )}

        {!user?.isPremium && (
          <div className="premium-cta">
            <p>{t('premium.ctaResult')}</p>
            <Link to="/premium" className="btn secondary">{t('premium.subscribe')}</Link>
          </div>
        )}
        {user?.isPremium && (
          <Link to="/practice" className="btn primary">{t('premium.play')}</Link>
        )}
      </div>
      <nav className="result-nav">
        <Link to="/leaderboard">Classement</Link>
        <Link to="/gallery">{t('gallery.title')}</Link>
        <Link to="/">Accueil</Link>
      </nav>
    </main>
  );
}
